import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import axiosInstance from '@/lib/axios/config/axiosConfig';

interface AgentMetricsExportButtonProps {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[];
  disabled?: boolean;
  className?: string;
}

const exportAgentMetrics = async (
  startDate: string,
  endDate: string,
  agentMicrosoftIds: string[] = [],
  setExportLoading: (loading: boolean) => void
) => {
  try {
    setExportLoading(true);
    
    const requestBody = {
      agentMicrosoftIds,
      startDate,
      endDate
    };

    const response = await axiosInstance.post('/AgentMetricsDetail/export', requestBody, {
      responseType: 'blob'
    });

    // Handle file download from backend
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'agent_metrics.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error exporting agent metrics:', error);
  } finally {
    setExportLoading(false);
  }
};

export const AgentMetricsExportButton: React.FC<AgentMetricsExportButtonProps> = ({
  startDate,
  endDate,
  agentMicrosoftIds,
  className = ""
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  
  const handleExport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (startDate && endDate && agentMicrosoftIds) {
      await exportAgentMetrics(
        startDate,
        endDate,
        agentMicrosoftIds,
        setExportLoading
      );
    }
  };

  return (
    <Button
      variant="outline"
      className={`h-8 text-xs flex items-center gap-1 [text-decoration:none] hover:bg-[#00FEC1] hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:border-[#00FEC1] dark:hover:text-black ${className}`}
      onClick={handleExport}
      disabled={(!startDate || !endDate || !agentMicrosoftIds || agentMicrosoftIds.length === 0) || exportLoading}
    >
      {exportLoading ? (
        <>
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Exporting...
        </>
      ) : (
        <>
          <Download className="h-3 w-3" />
          Export
        </>
      )}
    </Button>
  );
};

export default AgentMetricsExportButton;